import i18n from '../i18n';

/**
 * Review-period labelling — turns a review's `periodStart`/`periodEnd` into a human label that
 * says WHICH period was reviewed (dashboard "Recently Completed Reviews", 2026-07 request).
 *
 * PTC supervision periods are FOUR-month blocks, three per calendar year. The backend calls them
 * trimesters (`api/Services/TrimesterHelper.cs`, locked decision §0.2) and that name is kept
 * verbatim here so the two sides stay greppable:
 *
 *   T1 = Jan 1 – Apr 30 · T2 = May 1 – Aug 31 · T3 = Sep 1 – Dec 31
 *
 * A review's stored period does NOT always fill a whole block (e.g. a Jan 1 – Mar 31 quarterly
 * review sits INSIDE T1), so the label is built as "block · exact range" and the block prefix is
 * emitted only when start and end fall in the SAME block. A period that straddles two blocks gets
 * the range alone — never a block label that would misstate what was reviewed.
 *
 * Dates are read as calendar dates straight off the `YYYY-MM-DD` prefix of the ISO string rather
 * than via `new Date(...)`, so a UTC-midnight value (`2026-01-01T00:00:00Z`) can't drift into the
 * previous block for viewers west of UTC.
 */

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface CalendarDate {
  year: number;
  /** 1-12. */
  month: number;
  /** 1-31. */
  day: number;
}

/** Reads the leading `YYYY-MM-DD` of an ISO date/date-time string; null when absent or invalid. */
function parseCalendarDate(iso: string | null | undefined): CalendarDate | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** The bare trimester key ("T1"/"T2"/"T3") a month (1-12) falls in — mirrors TrimesterHelper.KeyForMonth. */
export function trimesterKeyForMonth(month: number): 'T1' | 'T2' | 'T3' {
  if (month <= 4) return 'T1';
  if (month <= 8) return 'T2';
  return 'T3';
}

function formatDay(d: CalendarDate, withYear: boolean): string {
  const base = `${MONTH_ABBR[d.month - 1]} ${d.day}`;
  return withYear ? `${base}, ${d.year}` : base;
}

/**
 * The period label for a review row, e.g.
 *   `T1 2026 · Jan 1 – Mar 31, 2026`   (period inside one trimester)
 *   `Jan 1 – Jun 30, 2026`             (period straddles T1 and T2 — no block prefix)
 *   `Nov 1, 2025 – Feb 28, 2026`       (period crosses the year boundary)
 *
 * Returns null when either bound is missing/unparseable, so callers simply render nothing rather
 * than a half-truth (legacy reviews and test fixtures may not carry both dates).
 */
export function reviewPeriodLabel(
  periodStart: string | null | undefined,
  periodEnd: string | null | undefined
): string | null {
  const start = parseCalendarDate(periodStart);
  const end = parseCalendarDate(periodEnd);
  if (!start || !end) return null;

  const sameYear = start.year === end.year;
  const range = `${formatDay(start, !sameYear)} – ${formatDay(end, true)}`;

  const sameTrimester =
    sameYear && trimesterKeyForMonth(start.month) === trimesterKeyForMonth(end.month);
  if (!sameTrimester) return range;

  return `${trimesterKeyForMonth(start.month)} ${start.year} · ${range}`;
}

/**
 * Validates the two bounds a review is about to be created with. Returns an error string to show
 * the user, or null when the period is well-formed.
 *
 * Mirrors the server rule in `api/Services/ReviewPeriodValidator.cs` — POST /api/client-reviews now
 * 400s on the same conditions, so this exists to say WHY in the dialog rather than to be the only
 * gate. Kept to the unambiguous invariant (both bounds present, end after start); trimester
 * alignment is deliberately NOT enforced, because full-year year-end reviews and pre-trimester
 * calendar quarters are legitimate.
 *
 * This matters because the create dialog PRE-FILLS a backwards period on its own: `periodStart`
 * comes from the day after the client's previous review ended while `periodEnd` is hard-coded to
 * the last day of the previous calendar month, and nothing ever compared the two. A client whose
 * last review already ran to the previous month-end gets `start > end` with no user input at all —
 * which is how `2026-03-01 .. 2026-02-28` reached Cosmos.
 *
 * Compared as `YYYY-MM-DD` strings, which is a chronological compare, so no timezone can shift a
 * bound across midnight.
 */
export function reviewPeriodBoundsError(
  periodStart: string | null | undefined,
  periodEnd: string | null | undefined
): string | null {
  const start = parseCalendarDate(periodStart);
  const end = parseCalendarDate(periodEnd);

  if (!start) return i18n.t('period.startRequired');
  if (!end) return i18n.t('period.endRequired');

  const s = `${periodStart}`.slice(0, 10);
  const e = `${periodEnd}`.slice(0, 10);

  if (e < s) return i18n.t('period.backwards');
  if (e === s) return i18n.t('period.sameDay');

  return null;
}
